import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import TransitionLink from 'components/transition-link/transition-link';
import Text from 'components/text/text';
import messages from './messages';

class DataModuleInfo extends React.Component {
    constructor(props) {
        super(props);

        const { intl } = props;

        this.emptyStates = {
            bike: intl.formatMessage(messages.about_bike),
            saddle: intl.formatMessage(messages.about_saddle),
            shoe: intl.formatMessage(messages.about_shoe),
        };
    }

    _renderTitleSet() {
        const { intl, data } = this.props;

        return (
            <div className="set-hd">
                <Text
                    className="mix-txt_capitalize"
                    color="Lightest"
                    tag="h3"
                    type="xl"
                    weight="light"
                >
                    {intl.formatMessage(messages[`${data.type}_label`])}
                </Text>
            </div>
        );
    }

    _renderTypeSet() {
        const { intl, data } = this.props;
        const date = intl.formatDate(data.updated_at, {
            month: 'numeric',
            day: '2-digit',
            year: 'numeric',
        });
        const styledSessionType = (
            <span className="txt mix-txt_capitalize">{data.session_type}</span>
        );

        return (
            <div className="set-bd">
                <Text color="DefaultGray" tag="span" weight="normal">
                    <FormattedMessage
                        {...messages.session_type}
                        values={{ sessionType: styledSessionType }}
                    />
                    <span className="txt mix-txt_colorDefaultBlue mix-txt_divider">
                        |
                    </span>
                    {date}
                </Text>
            </div>
        );
    }

    _renderDataSet(label, descriptor, value, unit) {
        return (
            <div className="set-bd set-bd_mega">
                <div className="set">
                    <div className="set-hd">
                        <Text
                            className="mix-txt_uppercase"
                            color="DefaultGray"
                            tag="h2"
                            weight="normal"
                        >
                            {label}
                        </Text>
                    </div>
                    <div className="set-bd set-bd_compact">
                        <Text
                            className="mix-txt_uppercase"
                            color="Lightest"
                            tag="span"
                            type="lg"
                            weight="light"
                        >
                            {descriptor}
                        </Text>
                    </div>
                    <div className="set-bd">
                        <Text
                            color="Lightest"
                            height="flat"
                            tag="span"
                            type="giant"
                            weight="light"
                        >
                            {value}
                            <span className="txt txt_xl mix-txt_lowercase">
                                {unit}
                            </span>
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    _renderDataSets() {
        const { intl, data } = this.props;
        const recommendedLabel = intl.formatMessage(messages.recommended_label);
        const startingLabel = intl.formatMessage(messages.starting_label);
        const bikeSizeLabel = intl.formatMessage(messages.bike_size_label);
        const saddleHeightLabel = intl.formatMessage(
            messages.saddle_height_label
        );
        const saddleSizeLabel = intl.formatMessage(messages.saddle_size_label);
        const showSizeLabel = intl.formatMessage(messages.shoe_size_label);
        const millimeterSymbol = intl.formatMessage(messages.millimeter_symbol);
        const centimeterSymbol = intl.formatMessage(messages.centimeter_symbol);

        let sets;

        switch (data.type) {
            case 'bike':
                sets = (
                    <div>
                        {this._renderDataSet(
                            recommendedLabel,
                            bikeSizeLabel,
                            data.bike_size,
                            data.mountain ? '' : centimeterSymbol
                        )}
                        {this._renderDataSet(
                            startingLabel,
                            saddleHeightLabel,
                            data.saddle_height,
                            millimeterSymbol
                        )}
                    </div>
                );
                break;
            case 'shoe':
                sets = this._renderDataSet(
                    recommendedLabel,
                    showSizeLabel,
                    data.shoe_size,
                    data.shoe_width
                );
                break;
            case 'saddle':
                sets = this._renderDataSet(
                    recommendedLabel,
                    saddleSizeLabel,
                    data.saddle_size,
                    millimeterSymbol
                );
                break;
            default:
                sets = '';
        }

        return sets;
    }

    _renderEmptyMessage() {
        return (
            <div className="set-bd set-bd_mega">
                <Text
                    color="DefaultGray"
                    height="medium"
                    tag="p"
                    type="md"
                    weight="normal"
                >
                    {this.emptyStates[this.props.data.type]}
                </Text>
            </div>
        );
    }

    _renderCTA() {
        const { data, intl } = this.props;

        let pathname;
        let text;
        if (data.empty) {
            pathname = '/locations';
            text = intl.formatMessage(messages.find_retul_technology_label);
        } else {
            pathname = `/product-matches?selectedProductType=${data.type}`;
            text = intl.formatMessage(
                messages[`view_${data.type.toLowerCase()}_matches_label`]
            );
        }

        return (
            <TransitionLink
                animateOnClick
                className="btn btn_dark btn_fixedWidth mix-btn_mobile"
                to={pathname}
            >
                {text}
            </TransitionLink>
        );
    }

    render() {
        const titleSet = this._renderTitleSet();
        const typeSet = this.props.data.empty ? '' : this._renderTypeSet();
        const dataSets = this.props.data.empty
            ? this._renderEmptyMessage()
            : this._renderDataSets();
        const cta = this._renderCTA();

        return (
            <div className="infoHighlight">
                <div className="infoHighlight-bar">
                    <span className="infoHighlight-bar-top"></span>
                    <span className="infoHighlight-bar-bottom"></span>
                </div>
                <div className="infoHighlight-info">
                    <div className="set">
                        {titleSet}
                        {typeSet}
                        {dataSets}
                        <div className="set-bd set-bd_super">{cta}</div>
                    </div>
                </div>
            </div>
        );
    }
}

const bikeDataShape = PropTypes.shape({
    bike_size: PropTypes.number,
    empty: PropTypes.bool,
    type: PropTypes.oneOf(['bike']).isRequired,
    updated_at: PropTypes.string,
});

const saddleDataShape = PropTypes.shape({
    empty: PropTypes.bool,
    saddle_size: PropTypes.number,
    type: PropTypes.oneOf(['saddle']).isRequired,
    updated_at: PropTypes.string,
});

const shoeDataShape = PropTypes.shape({
    empty: PropTypes.bool,
    shoe_size: PropTypes.number,
    shoe_width: PropTypes.string,
    type: PropTypes.oneOf(['shoe']).isRequired,
    updated_at: PropTypes.string,
});

DataModuleInfo.propTypes = {
    data: PropTypes.oneOfType([bikeDataShape, saddleDataShape, shoeDataShape])
        .isRequired,
};

export default injectIntl(DataModuleInfo);



// WEBPACK FOOTER //
// ./src/client/pages/body-data/_data-module-info.jsx